import type { Hold } from './api';

// GrailFind's words, the same as the server-rendered pages say them (api/lib/grail_web/controllers), so a buyer reads
// the same thing on either.

export const cards = (n: number) => (n === 1 ? '1 card' : `${n} cards`);

/** A price in the shop's money: "RM 4,520", "RM 8.86". */
export function money(cents: number, symbol: string): string {
  const frac = cents % 100;
  return `${symbol} ${Math.floor(cents / 100).toLocaleString('en-US')}${frac ? `.${String(frac).padStart(2, '0')}` : ''}`;
}

/** Each currency on its own: "RM 4,520", or "RM 4,520 + S$ 80" for shops in two countries. */
export function total(items: { cents: number | null; symbol: string; currency?: string }[]): string {
  const by = new Map<string, number>();
  for (const x of items) {
    if (typeof x.cents !== 'number' || x.cents < 0) continue;
    const key = x.symbol || x.currency || '';
    by.set(key, (by.get(key) ?? 0) + x.cents);
  }
  return [...by].map(([symbol, cents]) => money(cents, symbol)).join(' + ');
}

/** The card page's hold box: its title and line, or null when the buyer may ask (with `before` said first). */
export function holdOnCard(hold: Hold | null): { title: string; text: string; cancel: string } | { ask: string | null } {
  if (!hold) return { ask: null };
  if (hold.state === 'asked' && hold.open)
    return {
      title: 'Waiting for the shop',
      text: `You asked them to hold it for ${hold.minutes} min. This page shows their answer.`,
      cancel: 'Cancel request',
    };
  if (hold.state === 'held' && hold.open)
    return { title: `Held for you until ${hold.held_until_clock}`, text: 'Go to the table and give your name.', cancel: 'I no longer need it' };
  if (hold.state === 'asked') return { ask: 'The shop did not answer. You can ask again, or go to the table.' };
  if (hold.state === 'declined') return { ask: "The shop can't hold it this time. You can still go to the table." };
  if (hold.state === 'expired') return { ask: 'Your hold has ended.' };
  if (hold.state === 'released') return { ask: 'The shop ended your hold.' };
  return { ask: null };
}

/** A hold request's line on My list. */
export function holdOnList(hold: Hold): string {
  if (hold.state === 'asked') return hold.open ? `Waiting for the shop · ${hold.minutes} min` : 'No answer from the shop';
  if (hold.state === 'held') return `Held for you until ${hold.held_until_clock}`;
  if (hold.state === 'expired') return 'Your hold has ended';
  if (hold.state === 'declined') return "The shop can't hold it";
  if (hold.state === 'released') return 'The shop ended your hold';
  if (hold.state === 'cancelled') return 'You cancelled';
  return '';
}

/** Why a hold request did not go (the API's reason), in plain words. */
export function holdError(code: string, maxOpen = 5, maxPerDay = 20): string {
  switch (code) {
    case 'not_found':
      return 'This card is no longer for sale.';
    case 'bad_minutes':
      return 'Pick how long you need: 15, 30 or 60 minutes.';
    case 'bad_phone':
      return 'That phone number does not look right. Leave it empty, or type it with its country code.';
    case 'already_asked':
      return 'You already asked the shop to hold this card.';
    case 'too_many':
      return `You have ${maxOpen} open requests. Cancel one, or wait for an answer, then ask again.`;
    case 'too_many_today':
      return `You asked for ${maxPerDay} holds today. Ask again tomorrow.`;
    case 'shop_busy':
      return 'This shop has many requests right now. Ask again in a few minutes, or go to its table.';
    case 'offline':
      return 'No signal. Try again in a moment.';
    default:
      return 'That did not work. Try again.';
  }
}

/** The grader's own colour on its badge; the others are ink. */
export const GRADER_COLORS: Record<string, string> = { PSA: '#C8102E', CGC: '#1F5BB8', BGS: '#3F4754' };

/** The graders whose logo the site has (public/graders). */
export const LOGO_GRADERS = ['PSA', 'CGC', 'BGS', 'SGC', 'TAG', 'ACE'];
